import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { ShieldCheck, Pause, Play, Loader2, Clock, Save, Trash2, Download, Timer } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "-");

export const WhatsAppAntiBlockCard = () => {
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/whatsapp/queue/summary`);
      setSummary(r.data);
      setForm(f => f || r.data.settings);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/whatsapp/queue/settings`, {
        ...form,
        min_delay: Number(form.min_delay), max_delay: Number(form.max_delay),
        batch_size: Number(form.batch_size), batch_pause_min: Number(form.batch_pause_min),
        batch_pause_max: Number(form.batch_pause_max), daily_limit: Number(form.daily_limit),
      });
      setForm(r.data);
      toast.success("Configurações anti-bloqueio salvas");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const action = async (a) => {
    try {
      const r = await axios.post(`${API}/whatsapp/queue/${a}`);
      toast.success(r.data.message);
      load();
    } catch (e) {
      toast.error("Erro ao executar ação");
    }
  };

  if (!form) return null;
  const num = (k, label, min, max, step = 1) => (
    <div className="min-w-0">
      <Label className="text-xs">{label}</Label>
      <Input type="number" min={min} max={max} step={step} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-9" data-testid={`antiblock-${k}`} />
    </div>
  );

  return (
    <Card className="border-emerald-200 dark:border-emerald-800" data-testid="whatsapp-antiblock-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
          Anti-bloqueio (modo humano)
        </CardTitle>
        <CardDescription>
          Todas as mensagens (escalas, lembretes, confirmações) entram numa fila e são enviadas em segundo plano com intervalos aleatórios, pausas por lote, limite diário e simulação de "digitando...". Você não precisa manter a tela aberta.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status da fila */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
          <div className="p-2 rounded-lg bg-muted/50">
            <p className="text-[11px] text-muted-foreground">Na fila</p>
            <p className="text-lg font-bold" data-testid="antiblock-pending">{summary?.pendentes ?? 0}</p>
          </div>
          <div className="p-2 rounded-lg bg-muted/50">
            <p className="text-[11px] text-muted-foreground">Enviadas hoje</p>
            <p className="text-lg font-bold">{summary?.enviadas_hoje ?? 0}<span className="text-xs text-muted-foreground font-normal">/{summary?.limite_diario}</span></p>
          </div>
          <div className="p-2 rounded-lg bg-muted/50">
            <p className="text-[11px] text-muted-foreground">Falhas</p>
            <p className="text-lg font-bold text-red-600">{summary?.falhas ?? 0}</p>
          </div>
          <div className="p-2 rounded-lg bg-muted/50">
            <p className="text-[11px] text-muted-foreground">Previsão</p>
            <p className="text-sm font-semibold flex items-center justify-center gap-1 h-7"><Clock className="h-3.5 w-3.5" />{summary?.pendentes ? fmtTime(summary.previsao_fim) : "-"}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {summary?.pausado ? (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Fila pausada</Badge>
          ) : summary?.fora_janela ? (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Fora da janela · retoma {fmtTime(summary?.proxima_janela)}</Badge>
          ) : summary?.enviando_para ? (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Próximo: {summary.enviando_para} às {fmtTime(summary.proximo_envio)}</Badge>
          ) : (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Fila ativa</Badge>
          )}
          <div className="ml-auto flex gap-1">
            {summary?.pausado ? (
              <Button size="sm" variant="outline" onClick={() => action("resume")} data-testid="antiblock-resume"><Play className="h-3.5 w-3.5 mr-1" />Retomar</Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => action("pause")} data-testid="antiblock-pause"><Pause className="h-3.5 w-3.5 mr-1" />Pausar</Button>
            )}
            {summary?.pendentes > 0 && (
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => window.confirm("Cancelar todas as mensagens na fila?") && action("cancel-all")} data-testid="antiblock-cancel-all"><Trash2 className="h-3.5 w-3.5 mr-1" />Cancelar fila</Button>
            )}
          </div>
        </div>

        {/* Configurações */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {num("min_delay", "Intervalo mín. (s)", 10, 600)}
          {num("max_delay", "Intervalo máx. (s)", 15, 900)}
          {num("daily_limit", "Limite por dia", 10, 1000)}
          {num("batch_size", "Pausa longa a cada N msgs", 2, 100)}
          {num("batch_pause_min", "Pausa longa mín. (s)", 30, 3600)}
          {num("batch_pause_max", "Pausa longa máx. (s)", 60, 7200)}
          <div className="min-w-0">
            <Label className="text-xs">Janela início</Label>
            <Input type="time" value={form.window_start} onChange={(e) => setForm({ ...form, window_start: e.target.value })} className="h-9" data-testid="antiblock-window-start" />
          </div>
          <div className="min-w-0">
            <Label className="text-xs">Janela fim</Label>
            <Input type="time" value={form.window_end} onChange={(e) => setForm({ ...form, window_end: e.target.value })} className="h-9" data-testid="antiblock-window-end" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <label className="flex items-center justify-between gap-3 p-2.5 rounded-lg border cursor-pointer">
            <div>
              <p className="text-sm font-medium">Simular "digitando..."</p>
              <p className="text-[11px] text-muted-foreground">Requer o serviço WhatsApp atualizado</p>
            </div>
            <Switch checked={!!form.typing} onCheckedChange={(v) => setForm({ ...form, typing: v })} data-testid="antiblock-typing" />
          </label>
          <label className="flex items-center justify-between gap-3 p-2.5 rounded-lg border cursor-pointer">
            <div>
              <p className="text-sm font-medium">Variar saudação/despedida</p>
              <p className="text-[11px] text-muted-foreground">Evita mensagens 100% idênticas</p>
            </div>
            <Switch checked={!!form.variar_mensagens} onCheckedChange={(v) => setForm({ ...form, variar_mensagens: v })} data-testid="antiblock-vary" />
          </label>
        </div>
        <p className="text-[11px] text-muted-foreground flex items-start gap-1.5">
          <Timer className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Estimativa: 30 mensagens ≈ {Math.round((30 * (Number(form.min_delay) + Number(form.max_delay)) / 2 + Math.floor(29 / Math.max(1, Number(form.batch_size))) * (Number(form.batch_pause_min) + Number(form.batch_pause_max)) / 2) / 60)} min. Quanto mais devagar, menor o risco de restrição.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={save} disabled={saving} className="flex-1" data-testid="antiblock-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Salvar configurações
          </Button>
          <Button asChild variant="outline" className="flex-1" data-testid="antiblock-download-service">
            <a href="/downloads/whatsapp-service.zip" download>
              <Download className="h-4 w-4 mr-2" />Baixar serviço WhatsApp atualizado
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default WhatsAppAntiBlockCard;
